import React from "react";

import { Camera } from "@mui/icons-material";
import { colors } from "@rubi/ui";
import { GoogleMapsProvider } from "@rubi/ui";
import ReactDOMServer from "react-dom/server";
import { ElementFactory, Question, Serializer, SvgRegistry } from "survey-core";
import {
  localization,
  PropertyGridEditorCollection,
} from "survey-creator-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import CustomMapElement from "./CameraRegistryMapComponent";

export const CAMERA_REGISTRY_MAP_TYPE = "camera-registry-map";

export class CameraRegistryModel extends Question {
  constructor(name: string) {
    super(name);
  }

  onValueChanged() {
    super.onValueChanged();
  }

  getType() {
    return CAMERA_REGISTRY_MAP_TYPE;
  }

  getCoverageAreaCheck() {
    return this.getPropertyValue("coverageAreaCheck");
  }

  setCoverageAreaCheck(val: boolean) {
    this.setPropertyValue("coverageAreaCheck", val);
  }
}

// Guard against duplicate registration (can happen with HMR or multiple imports)
export function registerCameraRegistryMap() {
  if (
    !ElementFactory.Instance.getAllTypes().includes(CAMERA_REGISTRY_MAP_TYPE)
  ) {
    ElementFactory.Instance.registerElement(
      CAMERA_REGISTRY_MAP_TYPE,
      (name) => {
        return new CameraRegistryModel(name);
      },
    );
  }
}

// Specify display names for the question type and its properties
// i18n: builder label deferred
const locale = localization.getLocale("");
locale.qt[CAMERA_REGISTRY_MAP_TYPE] = "Camera Registry Map";
locale.pe.colorPickerType = "Camera Registry Map type";

// Register an SVG icon for the question type
const SURVEY_ICON_COLOR = colors.text.disabled;
const svg = ReactDOMServer.renderToString(
  <Camera style={{ fill: SURVEY_ICON_COLOR }} />,
);
SvgRegistry.registerIconFromSvg(CAMERA_REGISTRY_MAP_TYPE, svg);

// Guard against duplicate registration (can happen with HMR or multiple imports)
if (!Serializer.findClass(CAMERA_REGISTRY_MAP_TYPE)) {
  Serializer.addClass(
    CAMERA_REGISTRY_MAP_TYPE,
    [
      {
        name: "cameraLocations",
        default: [],
      },
      {
        name: "googleMapsPlace",
        default: {},
      },
      {
        name: "coverageAreaCheck",
        default: false,
        choices: [false, true],
        category: "general",
        visibleIndex: 2,
      },
    ],
    function () {
      return new CameraRegistryModel("");
    },
    "question",
  );
}

export class CameraRegistryMap extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    if (!this.question.value) {
      this.question.value = {
        googleMapsPlace: {},
        cameraLocations: [],
      };
    }

    this.state = { value: this.question.value };
  }

  get question(): CameraRegistryModel {
    return this.questionBase as CameraRegistryModel;
  }

  setFieldValue = (field: string, data: any) => {
    this.question.value = { ...this.question.value, [field]: data };
  };

  renderElement(): JSX.Element {
    return (
      <GoogleMapsProvider>
        <CustomMapElement
          cameraLocations={this.question.value?.cameraLocations}
          setCameraLocations={(val) => {
            if (val) {
              this.setFieldValue("cameraLocations", val);
            }
          }}
          address={this.question.value?.googleMapsPlace}
          setAddress={(val) => {
            this.setFieldValue("googleMapsPlace", val);
          }}
          disabled={this.props.isDisplayMode}
        />
      </GoogleMapsProvider>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  CAMERA_REGISTRY_MAP_TYPE,
  (props) => {
    return React.createElement(CameraRegistryMap, props);
  },
);

// Register property grid editor
PropertyGridEditorCollection.register({
  fit: function (prop: { type: string }) {
    return prop.type === "camera-registry-map";
  },
  getJSON: function () {
    return {
      type: CAMERA_REGISTRY_MAP_TYPE,
      cameraLocations: [],
      googleMapsPlace: {},
      coverageAreaCheck: false,
    };
  },
});
